/**
 * Note Quality Checks — flat Aswe-style list (green check / red fail).
 */
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  Box,
  Card,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconCheck, IconInfoCircle, IconListCheck } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import type {
  AiNoteCheckResult,
  NoteCheckFinding,
} from '@/pages/appointments/fhirNoteMapping';
import {
  DRAFT_CHECK_IDS,
  SERVER_CHECK_IDS,
  getCheckDisplayLabel,
} from '@/pages/appointments/noteCheckConstants';
import {
  getVisibleManualSupervisionChecks,
  type ManualSupervisionCheckItem,
} from '@/pages/appointments/supervisionManualChecks';

export type AutomaticReviewStatus = 'passed' | 'flagged' | 'review_recommended';

export interface SignReviewCheckItem {
  id: string;
  label: string;
  location?: string;
  scrollTarget?: string;
  detail?: string;
  findings?: NoteCheckFinding[];
  status: AutomaticReviewStatus;
}

export type AutomaticReviewRunState = 'not_started' | 'running' | 'completed' | 'failed';

export interface NoteQualityChecklistProps {
  draftChecks?: AiNoteCheckResult[];
  showDraftSection?: boolean;
  automaticChecks?: AiNoteCheckResult[];
  automaticReviewState?: AutomaticReviewRunState;
  automaticReviewCheckedAt?: string;
  automaticReviewError?: string;
  reviewIsStale?: boolean;
  signChecks?: SignReviewCheckItem[];
  showSignChecksSection?: boolean;
  onCheckNavigate?: (scrollTarget: string) => void;
  /** @deprecated Use onCheckNavigate */
  onSignCheckNavigate?: (scrollTarget: string) => void;
  showManualReview?: boolean;
  questionnaireResponse?: QuestionnaireResponse | null;
}

function resolveScrollTarget(check: {
  scrollTarget?: string;
  findings?: NoteCheckFinding[];
}): string | undefined {
  if (check.scrollTarget?.trim()) return check.scrollTarget;
  return check.findings?.find((finding) => finding.itemPath?.trim())?.itemPath;
}

function isPassing(status: AutomaticReviewStatus): boolean {
  return status === 'passed';
}

type FlatCheck = {
  key: string;
  id: string;
  label: string;
  status: AutomaticReviewStatus;
  scrollTarget?: string;
};

export function NoteQualityChecklist({
  draftChecks,
  showDraftSection = false,
  automaticChecks: automaticChecksProp,
  automaticReviewState = 'not_started',
  automaticReviewError,
  signChecks,
  showSignChecksSection = false,
  onCheckNavigate,
  onSignCheckNavigate,
  showManualReview = false,
  questionnaireResponse,
}: NoteQualityChecklistProps): JSX.Element {
  const navigateToCheck = onCheckNavigate ?? onSignCheckNavigate;

  const flatChecks = useMemo((): FlatCheck[] => {
    const rows: FlatCheck[] = [];
    const seen = new Set<string>();

    const push = (check: FlatCheck) => {
      if (seen.has(check.id)) return;
      seen.add(check.id);
      rows.push(check);
    };

    if (showDraftSection && draftChecks?.length) {
      for (const id of DRAFT_CHECK_IDS) {
        const check = draftChecks.find((c) => c.id === id);
        if (!check) continue;
        push({
          key: `draft-${check.id}`,
          id: check.id,
          label: getCheckDisplayLabel(check.id, check.status, check.label),
          status: check.status,
          scrollTarget: resolveScrollTarget(check),
        });
      }
    }

    if (automaticChecksProp?.length) {
      for (const id of SERVER_CHECK_IDS) {
        const check = automaticChecksProp.find((c) => c.id === id);
        if (!check) continue;
        push({
          key: `server-${check.id}`,
          id: check.id,
          label: getCheckDisplayLabel(check.id, check.status, check.label),
          status: check.status,
          scrollTarget: resolveScrollTarget(check),
        });
      }
    }

    if (showSignChecksSection) {
      for (const check of signChecks ?? []) {
        push({
          key: `sign-${check.id}`,
          id: check.id,
          label: getCheckDisplayLabel(check.id, check.status, check.label),
          status: check.status,
          scrollTarget: resolveScrollTarget(check),
        });
      }
    }

    return rows;
  }, [automaticChecksProp, draftChecks, showDraftSection, showSignChecksSection, signChecks]);

  const manualItems = useMemo((): ManualSupervisionCheckItem[] => {
    if (!showManualReview) return [];
    return getVisibleManualSupervisionChecks(questionnaireResponse);
  }, [showManualReview, questionnaireResponse]);

  const [manualChecked, setManualChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setManualChecked((prev) => {
      const next: Record<string, boolean> = {};
      for (const item of manualItems) {
        next[item.id] = prev[item.id] ?? false;
      }
      return next;
    });
  }, [manualItems]);

  const isRunning = automaticReviewState === 'running';

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group gap="xs">
          <IconListCheck size={16} />
          <Title order={5}>Quality Checks</Title>
          {isRunning ? <Loader size="xs" /> : null}
        </Group>

        {automaticReviewError ? (
          <Text size="xs" c="red">
            {automaticReviewError}
          </Text>
        ) : null}

        {flatChecks.length === 0 && !isRunning ? (
          <Text size="sm" c="dimmed">
            Checks appear as you edit and save the note.
          </Text>
        ) : (
          <Stack gap={8}>
            {flatChecks.map((check) => {
              const passed = isPassing(check.status);
              const canNavigate = Boolean(check.scrollTarget && navigateToCheck && !passed);
              const content = (
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <ThemeIcon
                    size={22}
                    radius="xl"
                    variant="light"
                    color={passed ? 'gray' : 'red'}
                    style={{ flexShrink: 0 }}
                  >
                    {passed ? <IconCheck size={14} /> : <IconInfoCircle size={14} />}
                  </ThemeIcon>
                  <Text
                    size="sm"
                    c={passed ? 'dark' : 'red'}
                    td={passed ? undefined : 'underline'}
                    style={{ lineHeight: 1.4 }}
                  >
                    {check.label}
                  </Text>
                </Group>
              );

              if (!canNavigate) {
                return <Box key={check.key}>{content}</Box>;
              }

              return (
                <UnstyledButton
                  key={check.key}
                  onClick={() => navigateToCheck?.(check.scrollTarget!)}
                  style={{ display: 'block', width: '100%', textAlign: 'left' }}
                >
                  {content}
                </UnstyledButton>
              );
            })}
          </Stack>
        )}

        {manualItems.length > 0 ? (
          <Stack gap={6} mt="xs">
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Manual review
            </Text>
            {manualItems.map((item) => (
              <Checkbox
                key={item.id}
                label={item.label}
                checked={Boolean(manualChecked[item.id])}
                onChange={(e) =>
                  setManualChecked((prev) => ({ ...prev, [item.id]: e.currentTarget.checked }))
                }
              />
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}
